import React, { useState, useEffect } from 'react';
import {
    FaPlus, FaEdit, FaTrash, FaUserShield, FaBan, FaSearch, FaSync,
    FaCheckSquare, FaSquare, FaInfoCircle
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import './css/BlackList.css';

const BlacklistSettings = () => {
    const [blacklist, setBlacklist] = useState([]);
    const [categories, setCategories] = useState([]); // Kategori listesi
    const [allBonuses, setAllBonuses] = useState([]); // Bonus listesi
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [formData, setFormData] = useState({
        username: '',
        restrictedCategoryIds: [],
        restrictedBonusTypes: [],
        reason: '',
        status: true
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [blRes, catRes, bonRes] = await Promise.all([
                axiosInstance.get('/blacklist/all'),
                axiosInstance.get('/bonus/selection-data'), // categoryId ve name döner
                axiosInstance.get('/bonus/all')    // bonusType ve name döner
            ]);
            setBlacklist(blRes.data);
            setCategories(catRes.data);
            setAllBonuses(bonRes.data);
        } catch (err) {
            toast.error("Veriler yüklenemedi!");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // Helper: ID'den Kategori İsmi Bulma
    const getCategoryName = (id) => {
        const cat = categories.find(c => c.categoryId === id);
        return cat ? (cat.name || cat.categoryName) : `ID: ${id}`;
    };

    // Helper: Type'dan Bonus İsmi Bulma
    const getBonusName = (type) => {
        const bon = allBonuses.find(b => b.bonusType === type);
        return bon ? bon.name : `Tip: ${type}`;
    };

    const handleToggle = (field, value) => {
        setFormData(prev => {
            const currentList = prev[field] || [];
            const isExist = currentList.includes(value);
            return {
                ...prev,
                [field]: isExist
                    ? currentList.filter(item => item !== value)
                    : [...currentList, value]
            };
        });
    };

    const handleOpenModal = (user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                username: user.username,
                restrictedCategoryIds: user.restrictedCategoryIds || [],
                restrictedBonusTypes: user.restrictedBonusTypes || [],
                reason: user.reason || '',
                status: user.status
            });
        } else {
            setEditingUser(null);
            setFormData({ username: '', restrictedCategoryIds: [], restrictedBonusTypes: [], reason: '', status: true });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/blacklist/save', formData);
            toast.success(editingUser ? "Güncellendi" : "Eklendi");
            setIsModalOpen(false);
            fetchData();
        } catch (err) { toast.error("Hata!"); }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Bu kullanıcının bonus engelini kaldırmak istediğinize emin misiniz?")) {
            try {
                await axiosInstance.delete(`/blacklist/${id}`);
                toast.success("Engel kaldırıldı.");
                fetchData();
            } catch (err) {
                toast.error("Silme işlemi başarısız.");
            }
        }
    };

    const filteredList = blacklist.filter(item =>
        item.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="data-page-container">
            <header className="header-card">
                <div className="header-info">
                    <h2>Bonus Blacklist Yönetimi <span className="count-badge">{blacklist.length}</span></h2>
                    <p>Kullanıcı bazlı kategori veya bonus kısıtlamalarını yönetin.</p>
                </div>
                <div className="action-group">
                    <button className="btn-modern btn-refresh" onClick={fetchData}><FaSync className={loading ? 'fa-spin' : ''} /></button>
                    <button className="btn-modern btn-add" onClick={() => handleOpenModal()}><FaPlus /> Kullanıcı Yasakla</button>
                </div>
            </header>

            <div className="search-bar-container">
                <FaSearch className="search-icon" />
                <input
                    placeholder="Kullanıcı ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="table-container">
                <table className="modern-table">
                    <thead>
                        <tr>
                            <th>KULLANICI</th>
                            <th>YASAKLI KATEGORİLER</th>
                            <th>YASAKLI BONUSLAR</th>
                            <th>NEDEN</th>
                            <th>TARİH</th>
                            <th style={{ textAlign: 'center' }}>İŞLEMLER</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.map((item) => (
                            <tr key={item._id} className="table-row">
                                <td className="user-name">
                                    <FaUserShield style={{ marginRight: '8px', color: '#e74c3c' }} />
                                    {item.username}
                                </td>
                                <td>
                                    <div className="bl-tag-container">
                                        {item.restrictedCategoryIds.map(id => (
                                            <span key={id} className="bl-tag bonus">{getCategoryName(id)}</span>
                                        ))}
                                    </div>
                                </td>
                                <td>
                                    <div className="bl-tag-container">
                                        {item.restrictedBonusTypes.map(type => (
                                            <span key={type} className="bl-tag bonus">{getBonusName(type)}</span>
                                        ))}
                                    </div>
                                </td>
                                <td><span className="reason-text">{item.reason}</span></td>
                                <td className="date-cell">{dayjs(item.createdAt).format('DD/MM/YYYY')}</td>
                                <td className="action-cells">
                                    <button className="btn-action edit" onClick={() => handleOpenModal(item)}><FaEdit /></button>
                                    <button className="btn-action reject" onClick={() => handleDelete(item._id)}><FaTrash /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {isModalOpen && (
                <div className="bl-modal-overlay">
                    <div className="bl-modal-wide">
                        <div className="bl-modal-header">
                            <h3>{editingUser ? <FaEdit /> : <FaPlus />} {editingUser ? 'Kısıtlamayı Düzenle' : 'Yeni Kullanıcı Yasakla'}</h3>
                            <button className="bl-close-btn" onClick={() => setIsModalOpen(false)}>&times;</button>
                        </div>

                        <form onSubmit={handleSubmit} className="bl-modal-form">
                            <div className="bl-form-top-row">
                                <div className="bl-form-group">
                                    <label>Kullanıcı Adı</label>
                                    <input
                                        className="bl-input-styled"
                                        value={formData.username}
                                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                        disabled={editingUser}
                                        placeholder="Örn: kullanıcı123"
                                        required
                                    />
                                </div>
                                <div className="bl-form-group">
                                    <label>Yasaklama Nedeni (Kısa Not)</label>
                                    <input
                                        className="bl-input-styled"
                                        value={formData.reason}
                                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                        placeholder="Örn: Bonus suistimali"
                                    />
                                </div>
                            </div>

                            <div className="bl-selection-grid">
                                <div className="bl-selection-box">
                                    <label><FaBan /> Yasaklı Kategoriler</label>
                                    <div className="bl-scroll-list">
                                        {categories.map(cat => (
                                            <div
                                                key={cat.categoryId}
                                                className={`bl-list-item ${formData.restrictedCategoryIds.includes(cat.categoryId) ? 'active' : ''}`}
                                                onClick={() => handleToggle('restrictedCategoryIds', cat.categoryId)}
                                            >
                                                {formData.restrictedCategoryIds.includes(cat.categoryId) ? <FaCheckSquare className="check-icon" /> : <FaSquare className="empty-icon" />}
                                                <span>{cat.name || cat.categoryName}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="bl-selection-box">
                                    <label><FaPlus /> Spesifik Bonuslar</label>
                                    <div className="bl-scroll-list">
                                        {allBonuses.map(bon => (
                                            <div
                                                key={bon.bonusType}
                                                className={`bl-list-item ${formData.restrictedBonusTypes.includes(bon.bonusType) ? 'active' : ''}`}
                                                onClick={() => handleToggle('restrictedBonusTypes', bon.bonusType)}
                                            >
                                                {formData.restrictedBonusTypes.includes(bon.bonusType) ? <FaCheckSquare className="check-icon" /> : <FaSquare className="empty-icon" />}
                                                <span>{bon.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="bl-modal-footer">
                                <button type="button" className="bl-btn-cancel-new" onClick={() => setIsModalOpen(false)}>İptal</button>
                                <button type="submit" className="bl-btn-submit-new">Kısıtlamayı Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlacklistSettings;